import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { readConsent, writeConsent } from "./consent";
import { track } from "../features/analytics/tracker";

export default function CookieConsent({ onConsentChange }) {
  const [open, setOpen] = useState(false);      // settings modal open
  const [showBanner, setShowBanner] = useState(false);

  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  const shownAt = useRef(null);

  useEffect(() => {
    const existing = readConsent();
    if (!existing) {
      setShowBanner(true);
      shownAt.current = Date.now();
      track("cookie_banner_shown", {}, true);
      return;
    }
    setAnalytics(!!existing.analytics);
    setMarketing(!!existing.marketing);
    setShowBanner(false);
  }, []);

  const persist = (next, source) => {
    const saved = writeConsent(next);
    setAnalytics(!!saved.analytics);
    setMarketing(!!saved.marketing);
    setShowBanner(false);
    setOpen(false);
    const msToDecide = shownAt.current ? Date.now() - shownAt.current : null;
    track("cookie_banner_decision", {
      choice: next.analytics && next.marketing ? "accept_all" : "reject_all",
      source, // "banner" | "settings"
      ms_to_decide: msToDecide,
    }, true);
    onConsentChange?.(saved);
  };

  const acceptAll = (source = "banner") => persist({ analytics: true, marketing: true }, source);
  const rejectAll = (source = "banner") => persist({ analytics: false, marketing: false }, source);

  const openSettings = () => {
    setOpen(true);
    track("cookie_banner_settings_open", {}, true);
  };

  const fade = useMemo(
    () => ({
      initial: { opacity: 0, y: 10 },
      animate: { opacity: 1, y: 0 },
      exit: { opacity: 0, y: 10 },
      transition: { duration: 0.25 },
    }),
    []
  );

  return (
    <>
      {/* Banner */}
      <AnimatePresence>
        {showBanner && !open && (
          <motion.div {...fade} className="cookie-banner" role="region" aria-label="Cookie consent">
            <div className="cookie-banner__inner">
              <div className="cookie-banner__text">
                <span className="cookie-banner__title">Cookies</span>{" "}
                <span className="muted">
                  We use necessary cookies to make the site work, and analytics with your permission.
                </span>
              </div>

              <div className="cookie-banner__actions">
                <button className="cookie-banner__btn cookie-banner__btn--ghost" onClick={openSettings}>
                  Settings
                </button>
                <button className="cookie-banner__btn cookie-banner__btn--ghost" onClick={() => rejectAll("banner")}>
                  Reject
                </button>
                <button className="cookie-banner__btn cookie-banner__btn--primary" onClick={() => acceptAll("banner")}>
                  Accept all
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal */}
      <AnimatePresence>
        {open && (
          <motion.div
            className="cookie-modal__backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
            aria-hidden="true"
          >
            <motion.div
              className="cookie-modal"
              initial={{ opacity: 0, scale: 0.98, y: 8 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.98, y: 8 }}
              transition={{ duration: 0.2 }}
              role="dialog"
              aria-modal="true"
              aria-label="Cookie settings"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="h3">Cookie settings</h3>
              <p className="muted">
                Necessary cookies are always on. You can choose whether we use analytics or marketing cookies.
              </p>

              <div className="cookie-rows">
                <div className="cookie-row">
                  <div>
                    <strong>Necessary</strong>
                    <div className="muted">Required for core site features.</div>
                  </div>
                </div>

                <div className="cookie-row">
                  <div>
                    <strong>Analytics</strong>
                    <div className="muted">Helps us understand usage and improve the site.</div>
                  </div>
                </div>
              </div>

              <div className="cookie-modal__actions">
                <button className="btn btn-ghost-cok" onClick={() => rejectAll("settings")}>
                  Reject all
                </button>
                <button className="btn btn-ghost-cok" onClick={() => acceptAll("settings")}>
                  Accept all
                </button>
              </div>

              <button className="cookie-modal__close" onClick={() => setOpen(false)} aria-label="Close">
                ✕
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Optional: expose a global opener (so footer can call window.openCookieSettings()) */}
      <CookieSettingsHook onOpen={() => setOpen(true)} />
    </>
  );
}

function CookieSettingsHook({ onOpen }) {
  useEffect(() => {
    window.openCookieSettings = onOpen;
    return () => {
      if (window.openCookieSettings === onOpen) delete window.openCookieSettings;
    };
  }, [onOpen]);
  return null;
}
